/**
 * InteractionEmailRenderer — async inbound-email hydration for agent-sourced
 * `send_email` approvals (Option A).
 *
 * Background: the canonical attention-item framework projects a tool call to
 * the frontend as `{ tool_name, args, preview_text? }`. Agent-sourced
 * `send_email` approvals therefore do NOT carry a `trigger_payload` with the
 * original email embedded the way legacy workflow/automation approvals did —
 * the args only reference the inbound message by `interaction_id`.
 *
 * This renderer bridges that gap: given `{ interaction_id }`, it fetches the
 * interaction + thread messages on demand (mirroring the inbox-manager
 * inspector's `fetchInteractionDetail` path), maps the selected message to the
 * shared `OriginalEmailData` shape, and delegates rendering to
 * `OriginalEmailRenderer` so the visual treatment (sender/subject header,
 * attachment chips, sandboxed HTML frame) is identical to the legacy path.
 *
 * States: loading skeleton → error notice → empty notice → full email. The
 * builder only emits this section when a non-empty `interaction_id` is
 * present, so the missing-id branch here is belt-and-braces.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Loader2 } from 'lucide-react'

import { fetchInteractionDetail } from '@/features/inbox-manager/api'
import { getMessageForInteraction } from '@/features/inbox-manager/components/MessageView'
import type { OriginalEmailData } from '../../../buildSyntheticDetail'
import { OriginalEmailRenderer } from './OriginalEmailRenderer'
import type { RendererProps } from './registry'

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function asNonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null
}

export function InteractionEmailRenderer({ data }: RendererProps) {
  const interactionId = isPlainObject(data)
    ? asNonEmptyString(data.interaction_id)
    : null

  const [state, setState] = React.useState<{
    status: 'loading' | 'ready' | 'error'
    email: OriginalEmailData | null
    error: string | null
  }>({ status: 'loading', email: null, error: null })

  React.useEffect(() => {
    if (!interactionId) {
      setState({ status: 'ready', email: null, error: null })
      return
    }

    let cancelled = false
    setState({ status: 'loading', email: null, error: null })

    fetchInteractionDetail(interactionId)
      .then(({ interaction, messages, errors }) => {
        if (cancelled) return

        const message = getMessageForInteraction(
          messages,
          interaction ?? undefined,
        )

        if (!message && !interaction) {
          // Nothing came back at all — surface the upstream error if one
          // was captured, otherwise fall through to the empty notice.
          const firstError = Object.values(errors)[0] ?? null
          setState({ status: 'ready', email: null, error: firstError })
          return
        }

        const email: OriginalEmailData = {
          from:
            asNonEmptyString(message?.from) ??
            asNonEmptyString(interaction?.from) ??
            undefined,
          from_name: asNonEmptyString(interaction?.from_name) ?? undefined,
          subject:
            asNonEmptyString(interaction?.subject) ??
            asNonEmptyString(message?.subject) ??
            undefined,
          html_body:
            asNonEmptyString(message?.html_body) ??
            asNonEmptyString(message?.body_html) ??
            undefined,
          plain_text_body:
            asNonEmptyString(message?.body) ??
            asNonEmptyString(message?.body_text) ??
            undefined,
          attachments: message?.attachments?.map((att) => ({
            id: att.id,
            filename: att.filename,
            content_type: att.content_type,
            size: att.size,
            ...(att.url ? { url: att.url } : {}),
          })),
        }

        setState({ status: 'ready', email, error: null })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          status: 'error',
          email: null,
          error: err instanceof Error ? err.message : String(err),
        })
      })

    return () => {
      cancelled = true
    }
  }, [interactionId])

  if (!interactionId) {
    return <p style={styles.notice}>Original email reference unavailable.</p>
  }

  if (state.status === 'loading') {
    return (
      <div style={styles.loading}>
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        <span>Loading original email…</span>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <p style={styles.notice}>
        Failed to load the original email
        {state.error ? `: ${state.error}` : '.'}
      </p>
    )
  }

  // Ready. Delegate to the shared renderer, which itself shows a muted
  // "content unavailable" notice when neither body field is present.
  return <OriginalEmailRenderer data={state.email} />
}

const styles: Record<string, CSSProperties> = {
  notice: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
}

export default InteractionEmailRenderer
